import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { NotificationPosition } from 'core/enums';
import { FormProvider } from 'core/states';
import { QuoteEntityType, QuoteStatus, UpsertQuoteAction } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { ICustomerQuote } from 'quotes/types';

import { mapUpdateQuotesForm } from './mappers';
import { updateQuotesSchema } from './schema';
import { IUpdateQuotesForm } from './types';
import { UpdateQuotesFormFields } from './UpdateQuotesFormFields';

interface IProps {
  isOpen: boolean;
  onClose?: () => void;
  quotes: ICustomerQuote[];
  onSubmitSuccess?: (data: ICustomerQuote) => void;
}

export const UpdateQuotes = ({ isOpen, onClose, quotes, onSubmitSuccess }: IProps) => {
  const { upsertCustomerQuotes } = useQuoteActions({
    entityType: QuoteEntityType.QUOTE,
    action: UpsertQuoteAction.UPDATE,
    notificationPosition: NotificationPosition.FORM,
  });

  const availableStatuses = useMemo(
    () => [
      QuoteStatus.DRAFT,
      QuoteStatus.LOST,
      QuoteStatus.OPEN,
      QuoteStatus.PUNCHED_OUT,
      QuoteStatus.WON,
    ],
    []
  );

  const onSubmit = (reset?: UseFormReset<IUpdateQuotesForm>) => async (data: IUpdateQuotesForm) => {
    const { success, quotes: updatedQuotes } = await upsertCustomerQuotes(
      mapUpdateQuotesForm(quotes, data)
    );

    if (!success) {
      return;
    }

    setTimeout(() => {
      onSubmitSuccess && onSubmitSuccess(updatedQuotes);

      handleOnClose(reset);
    }, 1000);
  };

  const handleOnClose = (reset: UseFormReset<IUpdateQuotesForm>) => {
    reset();
    onClose?.();
  };

  return (
    <FormProvider schema={updateQuotesSchema(availableStatuses)} resetDefaultValues>
      <UpdateQuotesFormFields
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        availableStatuses={availableStatuses}
      />
    </FormProvider>
  );
};
